import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { add, endOfDay, isAfter, isSameDay, parseISO, startOfDay } from 'date-fns';
import { Spacer } from 'design-system';
import { AppView } from 'design-system/AppView';
import BottomPanel from 'design-system/BottomPanel';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { Calendar } from 'design-system/calendar/Calendar';
import { Day } from 'design-system/calendar/Day';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import createStyleSheets from 'utils/createStyleSheets';
import { formatPauseAutoinvestDate } from '../utils';
import usePauseAndOptionallySkipAutoinvest from '../hooks/usePauseAndOptionallySkipAutoinvest';
import { PauseAutoinvestExpiryDateType } from '../types';
import { isWeb } from '../../../constants';
const PauseAutoinvestSelectDateScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const { planId, skipUpcoming, expectedPaymentDate, activityId } = route.params;
    const styles = useStyles(styleSet);
    const todayDate = useMemo(() => startOfDay(new Date()), []);
    const endOfToday = useMemo(() => endOfDay(new Date()), []);
    const [selected, setSelected] = useState(add(todayDate, { days: 1 }));
    const { handleSave, fetching } = usePauseAndOptionallySkipAutoinvest({
        planId,
        skipUpcoming,
        expectedPaymentDate,
        activityId,
        popCount: 3,
    });
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const checkEnabled = useCallback((date) => {
        const currentDate = parseISO(date.dateString);
        return isAfter(currentDate, endOfToday);
    }, [endOfToday]);
    const onDayPress = useCallback((date) => {
        if (!checkEnabled(date)) {
            return;
        }
        setSelected(parseISO(date.dateString));
    }, [checkEnabled]);
    const { paddingHorizontalStyle, contentWidth } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const onSubmit = useCallback(async () => {
        handleSave({
            finalDate: selected,
            type: PauseAutoinvestExpiryDateType.CUSTOM,
        });
    }, [handleSave, selected]);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView contentContainerStyle={paddingBottom} onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title="Select the date" subTitle={'Pause Autoinvest until\uD83D\uDC47'}/>
            <Calendar style={[styles.calendar, { maxWidth: contentWidth }]} dayComponent={({ date }) => (<Day key={date?.dateString} date={date} onDayPress={onDayPress} isDisabled={date ? !checkEnabled(date) : false} isStartDay={date ? isSameDay(selected, parseISO(date.dateString)) : false}/>)}/>
          </Animated.ScrollView>
          <BottomPanel>
            <View style={styles.bottomPanelTextContainer}>
              <Text TextThin-16 Secondary centered>
                Your Autoinvest will start{' '}
                <Text Text-16 Secondary>
                  {formatPauseAutoinvestDate(selected, true)}
                </Text>
              </Text>
            </View>
            <Spacer h={16}/>
            <Button brand onPress={onSubmit} title="Continue" isFetching={fetching}/>
          </BottomPanel>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    calendar: {
        width: '100%',
        alignSelf: 'center',
    },
    bottomPanelTextContainer: {
        paddingHorizontal: rem(24),
    },
}));
export default PauseAutoinvestSelectDateScreen;
